import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import { mentionDisplay } from '@src/utility/commentMentions';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { ClipAssignee } from '../hooks/useClipAssignees';
import type { MentionRef } from '@src/utility/commentMentions';
import type { ChangeEvent, KeyboardEvent, RefObject } from 'react';

export type MentionPerson = { id: string; name: string; email: string };

/** A teammate is mentionable only if we have a stable id + a display name. */
export function toMentionPeople(assignees: ClipAssignee[]): MentionPerson[] {
  return assignees
    .filter((a): a is Required<ClipAssignee> => !!a.id && !!a.name)
    .map(a => ({ id: a.id, name: a.name, email: a.email }));
}

// An active `@query` is an `@` at a word boundary followed by word chars, up to
// the caret (no whitespace between). Group 1 is the partial query — a bare `@`
// (empty query) lists the full roster.
const QUERY_RE = /(?:^|\s)@(\w*)$/;
const MAX_RESULTS = 6;

/**
 * `@`-mention autocomplete for a controlled textarea. Detects an `@query` at the
 * caret, filters `people`, and on select splices a `@[Name](id)` token in.
 *
 * Wiring: the textarea's `onChange` calls {@link handleChange} (it also updates
 * the value), and `onKeyDown` calls {@link handleKeyDown} first — if it returns
 * `true` the key was consumed (nav/select/close) and the caller must not run its
 * own handler (e.g. Enter-to-post).
 */
export function useMentionAutocomplete(
  value: string,
  setValue: (next: string) => void,
  textareaRef: RefObject<HTMLTextAreaElement>,
  people: MentionPerson[],
) {
  // null = closed; a string (possibly '') = the active partial query.
  const [query, setQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Index of the '@' that starts the active query — where the token replaces to.
  const startRef = useRef(0);
  // People the user actually picked from the popover. The editor holds only the
  // display form (`@Name`), so at submit time these carry the id a name alone
  // can't resolve — two teammates can share a display name.
  const picksRef = useRef<MentionRef[]>([]);

  const results = useMemo(() => {
    if (query == null) return [];
    const q = query.toLowerCase();
    return people
      .filter(p => p.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, MAX_RESULTS);
  }, [query, people]);

  const open = query != null && results.length > 0;

  const close = useCallback(() => setQuery(null), []);

  // Anchor above the composer; width defaults to the composer's own width so a
  // teammate's name + email fit on one row.
  const { menuRef, style: menuStyle } = useAnchoredDropdown(
    open,
    textareaRef,
    close,
    240,
    { placement: 'above' },
  );

  const syncFromCaret = useCallback((next: string, caret: number) => {
    const match = next.slice(0, caret).match(QUERY_RE);
    if (match) {
      startRef.current = caret - match[1].length - 1;
      setActiveIndex(0);
      setQuery(match[1]);
    } else {
      setQuery(null);
    }
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value;
      setValue(next);
      // Drop picks whose display form the draft no longer contains (edited away,
      // or cleared after a post) so a stale id can never be applied to a name
      // the user later types by hand.
      picksRef.current = picksRef.current.filter(p =>
        next.includes(mentionDisplay(p.name)),
      );
      syncFromCaret(next, e.target.selectionStart ?? next.length);
    },
    [setValue, syncFromCaret],
  );

  const select = useCallback(
    (person: MentionPerson) => {
      const caret = textareaRef.current?.selectionStart ?? value.length;
      const start = startRef.current;
      // Insert the friendly display form (`@Name`); the storable
      // `@[Name](id)` token is derived at submit time via
      // `encodeMentionTokens` so the editor never shows raw tokens.
      const token = `${mentionDisplay(person.name)} `;
      setValue(value.slice(0, start) + token + value.slice(caret));
      // Remember who was picked so submit resolves this exact person rather
      // than re-guessing from the display name (see `picks`).
      picksRef.current = [
        ...picksRef.current.filter(p => p.id !== person.id),
        { id: person.id, name: person.name },
      ];
      setQuery(null);
      // Restore focus + place the caret just after the inserted token.
      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        const pos = start + token.length;
        node.setSelectionRange(pos, pos);
      });
    },
    [setValue, textareaRef, value],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!open) return false;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex(i => (i + 1) % results.length);
          return true;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex(i => (i - 1 + results.length) % results.length);
          return true;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          select(results[activeIndex]);
          return true;
        case 'Escape':
          e.preventDefault();
          setQuery(null);
          return true;
        default:
          return false;
      }
    },
    [open, results, activeIndex, select],
  );

  // Forget the picks when the editor switches to an unrelated draft (e.g. the
  // edit box moving to another comment) — otherwise a pick made in the previous
  // draft would still override the roster for the same display name here.
  const resetPicks = useCallback(() => {
    picksRef.current = [];
  }, []);

  return {
    open,
    results,
    activeIndex,
    setActiveIndex,
    menuRef,
    menuStyle,
    handleChange,
    handleKeyDown,
    select,
    resetPicks,
    /**
     * The people picked from the popover and still present in the draft. Pass
     * these to `encodeMentionTokens` *after* the roster (its by-name map is
     * last-wins) so a pick beats the roster's guess for that name.
     */
    picks: picksRef.current,
  };
}
